import React, { type ReactNode } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { statusCategoryForId } from '@jira/platform__status-categories/src/status-categories.tsx';
import type {
	ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges$key,
	ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges$data,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges.graphql';
import type {
	ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion$key,
	ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion$data,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion.graphql';
import type {
	ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue$key,
	ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue$data,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue.graphql';
import type {
	ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion$key,
	ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion$data,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion.graphql';

interface ResultProps {
	hasWarnings: boolean;
	hasOpenPullRequestWarning: boolean;
	hasFailingBuildsWarning: boolean;
	hasUnreviewedCodeWarning: boolean;
	hasOpenReviewWarning: boolean;
}

interface Props {
	jiraVersion: ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion$key;
	jiraIssue: ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue$key;
	children: (props: ResultProps) => ReactNode;
}

type NodeIssueDevSummary = NonNullable<
	ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges$data[0]['node']
>;

type IssueDevSummary =
	| ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue$data
	| NodeIssueDevSummary;

type VersionData =
	| ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion$data
	| ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion$data;

const getDevSummaryDataWarnings = (issue: IssueDevSummary, jiraVersionData: VersionData) => {
	const fields = issue.fieldsById.edges.map((edge) => edge?.node).filter(Boolean);
	const jiraStatusField = fields.find((fieldNode) => fieldNode?.fieldId === 'status');
	const issueStatus = statusCategoryForId(
		jiraStatusField?.status?.statusCategory?.statusCategoryId,
	);

	const build = issue.devSummaryCache?.devSummary.build;
	const commit = issue.devSummaryCache?.devSummary.commit;
	const pullrequest = issue.devSummaryCache?.devSummary.pullrequest;
	const pullrequestCount = issue.devSummaryCache?.devSummary.pullrequest?.overall.count ?? 0;
	const reviewState = issue.devSummaryCache?.devSummary?.review?.overall?.state;
	const reviewCount = issue.devSummaryCache?.devSummary?.review?.overall?.count ?? 0;

	const openPullRequest = jiraVersionData.warningConfig?.openPullRequest;
	const failingBuild = jiraVersionData.warningConfig?.failingBuild;
	const unreviewedCode = jiraVersionData.warningConfig?.unreviewedCode;
	const openReview = jiraVersionData.warningConfig?.openReview;

	const failingBuildCount =
		(build?.overall.failedBuildCount ?? 0) +
		((issue.devOpsSummarisedEntities?.summarisedBuilds?.buildStates ?? []).find(
			(buildState) => buildState?.state === 'FAILED',
		)?.count ?? 0);

	const hasOpenPullRequestWarning =
		issueStatus === 'DONE' &&
		openPullRequest === 'ENABLED' &&
		pullrequest?.overall?.state === 'OPEN' &&
		pullrequest.overall.count > 0;
	const hasFailingBuildsWarning =
		issueStatus === 'DONE' && failingBuild === 'ENABLED' && build != null && failingBuildCount > 0;
	const hasUnreviewedCodeWarning =
		issueStatus === 'DONE' &&
		unreviewedCode === 'ENABLED' &&
		pullrequestCount === 0 &&
		reviewCount === 0 &&
		commit != null &&
		commit.overall.count > 0;
	const hasOpenReviewWarning =
		issueStatus === 'DONE' &&
		openReview === 'ENABLED' &&
		reviewState != null &&
		!['CLOSED', 'REJECTED'].includes(reviewState);
	const hasWarnings =
		issueStatus === 'DONE' &&
		(hasOpenPullRequestWarning ||
			hasFailingBuildsWarning ||
			hasUnreviewedCodeWarning ||
			hasOpenReviewWarning);

	return {
		hasWarnings,
		hasOpenPullRequestWarning,
		hasFailingBuildsWarning,
		hasUnreviewedCodeWarning,
		hasOpenReviewWarning,
	};
};

const getIssueNodeWarningsCount = (
	edges: ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges$data,
	jiraVersionData: ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion$data,
): number =>
	edges
		.map((edge) => edge.node)
		.filter(Boolean)
		.reduce((acc, issue) => {
			const warningResult = getDevSummaryDataWarnings(issue, jiraVersionData);
			if (warningResult.hasWarnings) {
				return acc + 1;
			}
			return acc;
		}, 0);

const getIssueWarnings = (
	issue: ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue$data,
	jiraVersionData: ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion$data,
) => getDevSummaryDataWarnings(issue, jiraVersionData);

export const IssueWarningProvider = ({
	jiraVersion,
	jiraIssue,
	children,
}: Props): React.JSX.Element => {
	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue on JiraIssue {
				fieldsById(ids: ["priority", "summary", "assignee", "status", "issuetype"])
					@required(action: THROW) {
					edges @required(action: THROW) {
						node {
							fieldId @required(action: THROW)
							... on JiraStatusField {
								status {
									statusCategory {
										statusCategoryId
									}
								}
							}
						}
					}
				}
				devOpsSummarisedEntities @skip(if: $isDevopsFeatureDisabledInFedRamp) {
					summarisedBuilds {
						count
						buildStates {
							state
							count
						}
					}
				}
				devSummaryCache @skip(if: $isDevopsFeatureDisabledInFedRamp) {
					devSummary @required(action: NONE) {
						pullrequest @required(action: NONE) {
							overall @required(action: NONE) {
								state @required(action: NONE)
								count @required(action: NONE)
							}
						}
						commit @required(action: NONE) {
							overall @required(action: NONE) {
								count @required(action: NONE)
							}
						}
						build @required(action: NONE) {
							overall @required(action: NONE) {
								failedBuildCount @required(action: NONE)
							}
						}
						review {
							overall {
								state
								count
							}
						}
					}
				}
			}
		`,
		jiraIssue,
	);

	const jiraVersionData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion on JiraVersion {
				warningConfig {
					openPullRequest @required(action: NONE)
					openReview @required(action: NONE)
					unreviewedCode @required(action: NONE)
					failingBuild @required(action: NONE)
				}
			}
		`,
		jiraVersion,
	);

	const warningFlags = getIssueWarnings(issue, jiraVersionData);

	return <>{children(warningFlags)}</>;
};

interface IssueWarningCountProviderProps {
	jiraVersion: ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion$key;
	issueEdges: ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges$key;
	children: (warningCount: Number) => ReactNode;
}

export const IssuesWarningCountProvider = ({
	jiraVersion,
	issueEdges,
	children,
}: IssueWarningCountProviderProps): React.JSX.Element => {
	const jiraIssueEdgeReferenceData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges on JiraIssueEdge
			@relay(plural: true) {
				node {
					fieldsById(ids: ["priority", "summary", "assignee", "status", "issuetype"])
						@required(action: THROW) {
						edges @required(action: THROW) {
							node {
								fieldId @required(action: THROW)
								... on JiraStatusField {
									status {
										statusCategory {
											statusCategoryId
										}
									}
								}
							}
						}
					}

					devOpsSummarisedEntities @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						summarisedBuilds {
							count
							buildStates {
								state
								count
							}
						}
					}

					devSummaryCache @required(action: NONE) @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						devSummary @required(action: NONE) {
							pullrequest @required(action: NONE) {
								overall @required(action: NONE) {
									state @required(action: NONE)
									count @required(action: NONE)
								}
							}
							review @required(action: NONE) {
								overall @required(action: NONE) {
									count @required(action: NONE)
									state
								}
							}
							commit @required(action: NONE) {
								overall @required(action: NONE) {
									count @required(action: NONE)
								}
							}
							build @required(action: NONE) {
								overall @required(action: NONE) {
									count @required(action: NONE)
									failedBuildCount @required(action: NONE)
								}
							}
						}
					}
				}
			}
		`,
		issueEdges,
	);

	const warningConfig = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion on JiraVersion {
				warningConfig {
					openPullRequest @required(action: NONE)
					openReview @required(action: NONE)
					unreviewedCode @required(action: NONE)
					failingBuild @required(action: NONE)
				}
			}
		`,
		jiraVersion,
	);

	const warningCount = getIssueNodeWarningsCount(jiraIssueEdgeReferenceData, warningConfig);

	return <>{children(warningCount)}</>;
};
